import { createContext, useContext, useState, useEffect } from "react";
import { api, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    // Mount-only bootstrap: hydrate user from the stored JWT (if any).
    // No dynamic deps: `setUser` / `setInitializing` are stable, and the api
    // module is a singleton import.
    const token = localStorage.getItem("vg_token");
    if (!token) {
      setUser(false);
      setInitializing(false);
      return;
    }
    setAuthToken(token);
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("vg_token");
        setAuthToken(null);
        setUser(false);
      })
      .finally(() => setInitializing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("vg_token", data.access_token);
    setAuthToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    // If the backend requires email verification, DO NOT store a token yet.
    // The caller (Register page) navigates to /verify-email.
    if (data && data.requires_verification) {
      return { requires_verification: true, email: data.email || email };
    }
    // Otherwise this is a legacy/idempotent path returning a real AuthResponse.
    if (data && data.access_token) {
      localStorage.setItem("vg_token", data.access_token);
      setAuthToken(data.access_token);
      setUser(data.user);
      return { requires_verification: false, user: data.user };
    }
    return { requires_verification: true, email };
  };

  const finishVerification = async (email, code) => {
    const { data } = await api.post("/auth/verify-email", { email, code });
    localStorage.setItem("vg_token", data.access_token);
    setAuthToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const finishReset = async (email, code, newPassword) => {
    const { data } = await api.post("/auth/reset-password", {
      email,
      code,
      new_password: newPassword,
    });
    localStorage.setItem("vg_token", data.access_token);
    setAuthToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("vg_token");
    setAuthToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, finishVerification, finishReset, logout, initializing }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
